// Self-heal for stale-bundle errors after a frontend deploy.
//
// An already-open tab still references the PREVIOUS build's hashed chunks (e.g.
// api-loUr9opL.js). After a deploy those files are gone, and the SPA nginx serves index.html
// (HTML) for the 404 — so a lazy import() either rejects (HTML parsed as a module) or resolves
// to a module whose `.default` is undefined → React.lazy throws
// "Cannot read properties of undefined (reading 'default')". Neither a component re-render nor
// the ErrorBoundary "retry" can fix it; only a full reload fetches the fresh index.html +
// current chunks. Reload ONCE (guarded) so a genuinely-broken build can't loop forever.

const RELOAD_KEY = 'asd:chunkReloadAt';
const RELOAD_WINDOW_MS = 30_000;

/** Heuristic: does this error look like a missing/stale chunk after a deploy? */
export function isStaleChunkError(err: unknown): boolean {
  const anyErr = err as any;
  const msg = String((anyErr && anyErr.message) || err || '').toLowerCase();
  const name = String((anyErr && anyErr.name) || '').toLowerCase();
  return (
    name === 'chunkloaderror' ||
    msg.includes("reading 'default'") ||               // React.lazy on a module without a default
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('failed to load module script') ||     // server returned HTML content-type
    (msg.includes('unexpected token') && msg.includes('<')) // HTML ("<!doctype") parsed as JS
  );
}

/**
 * Reload the page once to recover from a stale bundle. Returns true if a reload was triggered.
 * Guarded to at most one reload per RELOAD_WINDOW_MS so a truly broken deploy surfaces the
 * error instead of hard-looping.
 */
export function reloadOnceForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || '0');
    const now = Date.now();
    if (now - last > RELOAD_WINDOW_MS) {
      sessionStorage.setItem(RELOAD_KEY, String(now));
      window.location.reload();
      return true;
    }
    return false;
  } catch {
    // sessionStorage unavailable (private mode / blocked) — reload without the guard.
    window.location.reload();
    return true;
  }
}

/**
 * Wrap a React.lazy import factory so a failed dynamic import (missing chunk after a deploy)
 * triggers a one-time reload instead of a permanent crash. On the rare genuine failure it
 * re-throws after the reload guard is exhausted, so the ErrorBoundary still shows.
 */
export function withChunkReload<T>(factory: () => Promise<T>): () => Promise<T> {
  return () =>
    factory().catch((err) => {
      if (isStaleChunkError(err) && reloadOnceForStaleChunk()) {
        // Stall until the reload happens so nothing renders against the broken module.
        return new Promise<T>(() => {});
      }
      throw err;
    });
}
